import CloseIcon from "@mui/icons-material/Close";
import CancelIcon from "@mui/icons-material/Cancel";
import {
    Box,
    Button,
    Dialog,
    IconButton,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import { Building, FileText, ReceiptText } from "lucide-react";
import { useForm } from "react-hook-form";
import CustomDropdownField from "../../common/CustomDropdownFiled";
import { useModalStyles } from "../../common/useModalStyles";
import { useEffect, useMemo, useState } from "react";
import { useAppDispatch } from "../../../redux/slice/redux-hooks";
import { fetchPracticeLookupByUserRole } from "../../../redux/slice/commonSlice";
import { USER_ROLE_ENTITY_NAMES } from "../../../constants/applicationConstants";
import { selectIsValueFoundInToken, useAuthSlice } from "../../../redux/slice/authSlice";
import { useSelector } from "react-redux";
import { RootState } from "../../../redux/store";
import { DomainAdd, OpenInNew } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";

interface ManagePracticeModalProps {
    open: boolean;
    onClose: () => void;
    onSubmit: (data: practiceFormData) => void;
    showAddPractice?: boolean;
}
export type practiceFormData = {
    practice: string | number;
}

const ManagePracticeModal = ({ open, onClose, onSubmit, showAddPractice = true }: ManagePracticeModalProps) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const {
        tokens,
        paperSx,
        headerSx,
        iconBoxSx,
        bodySx,
        footerSx,
        closeIconSx,
        dropdownWrapSx,
        primaryBtnSx,
        cancelBtnSx,
    } = useModalStyles();

    const {
        control,
        handleSubmit,
        reset,
        setValue,
        watch,
        getValues,
        formState: { errors },
    } = useForm<practiceFormData>({
        defaultValues: {
            practice: ""
        },
        mode: "onSubmit",
    });

    const outlineBtn = {
        textTransform: "none" as const,
        fontSize: "0.8rem",
        fontWeight: 600,
        color: tokens.textSecondary,
        border: `1px solid ${tokens.border}`,
        borderRadius: "8px",
        px: 1.8,
        py: 0.6,
        backgroundColor: "transparent",
        "&:hover": {
            borderColor: tokens.accent,
            color: tokens.accent,
            backgroundColor: "rgba(20,184,166,0.05)",
        },
    };

    const dispatch = useAppDispatch();
    const [practiceList, setPracticeList] = useState<any[]>([]);
    const { userInfo } = useAuthSlice();
    const navigate = useNavigate();

    const handleClose = () => {
        setTimeout(() => {
            reset();
            onClose();
        }, 300);
    };
    const onFormSubmit = (data: practiceFormData) => {
        onSubmit(data);
        handleClose();
    };

    const FieldLabel = ({
        label,
        required = false,
    }: {
        label: string;
        required?: boolean;
    }) => (
        <Typography
            sx={{
                fontSize: "0.8rem",
                fontWeight: 600,
                color: tokens.textSecondary,
                mb: 0.7,
                display: "flex",
                alignItems: "center",
                gap: 0.3,
            }}
        >
            {label}
            {required && (
                <Box component="span" sx={{ color: tokens.accent }}>
                    *
                </Box>
            )}
        </Typography>
    );

    type Option = {
        value: string | number;
        label: string;
    };

    const handlePracticeChange = async () => {
        const practice = getValues('practice');
    };

    const practiceOptions = useMemo(() => {
        let options: Option[] = [];
        practiceList.map((practice) => {
            const option: Option = {
                label: practice.practiceName,
                value: practice.practiceId,
            };
            options.push(option);
        }
        );
        if (practiceList && practiceList?.length == 1) {
            setValue('practice', practiceList[0].practiceId);
            handlePracticeChange();
        }
        return options;
    }, [practiceList]);

    useEffect(() => {
        if (open) fetchPractices();
        else handleClose();
    }, [open]);

    const fetchPractices = async () => {
        const params: any = {
            activePracticeOnly: true,
        };

        const response = await dispatch(fetchPracticeLookupByUserRole(params));

        if (response.meta.requestStatus === "fulfilled" && response.payload) {
            const practices = response.payload;
            setPracticeList(practices);

            if (
                userInfo?.entityName === USER_ROLE_ENTITY_NAMES.PRACTICE &&
                practices.length === 1
            ) {
                setValue("practice", practices[0].practiceId);
            }
        }
    };

    const handleAddNewPractice = () => {
        navigate("/practice")
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            fullScreen={isMobile}
            PaperProps={{ sx: paperSx(isMobile) }}
        >
            <form onSubmit={handleSubmit(onFormSubmit)}>
                <Box sx={headerSx}>
                    <Box sx={iconBoxSx(tokens.accent)}>
                        {showAddPractice
                            ? <Building size={20} color={tokens.accent} strokeWidth={1.8} />
                            : <FileText size={20} color={tokens.accent} strokeWidth={1.8} />
                        }
                    </Box>
                    <Box>
                        <Typography
                            fontWeight={700}
                            sx={{
                                fontSize: "1.1rem",
                                color: tokens.textPrimary,
                                lineHeight: 1.3,
                            }}
                        >
                            {showAddPractice ? "Manage Practice" : "Create Agreement"}
                        </Typography>
                        <Typography
                            sx={{ fontSize: "0.8rem", color: tokens.textSecondary, mt: 0.15 }}
                        >
                            {showAddPractice ? "Select a practice a manage" : "Select a practice to continue"}
                        </Typography>
                    </Box>
                    <IconButton onClick={handleClose} size="small" sx={closeIconSx}>
                        <CloseIcon sx={{ fontSize: "1rem" }} />
                    </IconButton>
                </Box>

                <Box sx={bodySx}>
                    <Box>
                        <FieldLabel label="Practice Name" required />
                        <Box sx={dropdownWrapSx()}>
                            <CustomDropdownField
                                name="practice"
                                control={control}
                                options={practiceOptions}
                                size="small"
                                placeholderText={
                                    practiceOptions.length === 0
                                        ? "No Practice found"
                                        : "Choose a practice..."
                                }
                                sortOptions={false}
                                disabled={practiceOptions?.length === 1}
                                onChangeSelect={() => handlePracticeChange()}
                                rules={{ required: "Please select a practice to continue." }}
                            />
                        </Box>
                        {showAddPractice && (
                            <Button
                                startIcon={
                                    <DomainAdd sx={{ fontSize: "0.95rem !important" }} />
                                }
                                size="small"
                                sx={{ ...outlineBtn, mt: 1 }}
                                onClick={handleAddNewPractice}
                            >
                                Add New Practice
                            </Button>
                        )}
                    </Box>
                </Box>

                <Box sx={footerSx}>
                    <Box sx={{ display: "flex", gap: 1.5 }}>
                        <Button
                            fullWidth
                            variant="contained"
                            type='submit'
                            startIcon={
                                <OpenInNew sx={{ fontSize: "1rem !important" }} />
                            }
                            // onClick={}
                            sx={primaryBtnSx}
                        >
                            {showAddPractice ? "Manage Practice" : "Create Agreement"}
                        </Button>

                        <Button
                            fullWidth
                            variant="outlined"
                            startIcon={<CancelIcon sx={{ fontSize: "1rem !important" }} />}
                            onClick={handleClose}
                            sx={cancelBtnSx}
                        >
                            Cancel
                        </Button>
                    </Box>
                </Box>
            </form>
        </Dialog>
    );
};

export default ManagePracticeModal;
